// Desk and touch controls. Aim with A/D or the arrows, club with W/S, hold Space (or the swing
// button) to charge and release to swing. The meter rises and falls like the old games because a
// player should be able to hit a soft shot on purpose. VR swings live in xr.ts.
//
// 2026-09-13 — the meter learned the difference between a drive and a putt. It used to fill in
// 0.8 s for every club, and 100 % on the putter is 7 m/s, so a two-metre putt lived in a fifty-
// millisecond window and almost never dropped. Now a full swing fills in 1.0 s, the putter in
// 1.6 s, and the putter's 100 % is scaled to the putt in front of you (a little past the hole),
// so dead weight sits around the middle of the meter. `power` stays the meter fraction the HUD
// draws; `shotPower()` is what goes to the server.

import { CLUB_ORDER, puttPowerFor, type Club } from '@shared/physics';

export class Input {
  club: Club = 'driver';
  aimDeg = 0;
  charging = false;
  power = 0;
  private chargeStart = 0;
  private held = new Set<string>();

  constructor(
    private onSwing: (club: Club, aimDeg: number, power: number) => void,
    private canSwing: () => boolean,
    /** metres from the ball to the cup, for the putter's scale; null when unknown */
    private distToCup: () => number | null = () => null,
  ) {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) { this.held.add(e.code); return; }
      this.held.add(e.code);
      if (e.code === 'Space') { e.preventDefault(); this.swingStart(); }
      if (e.code === 'KeyW') this.clubPrev();
      if (e.code === 'KeyS') this.clubNext();
    });
    window.addEventListener('keyup', (e) => {
      this.held.delete(e.code);
      if (e.code === 'Space') { e.preventDefault(); this.swingEnd(); }
    });
  }

  setClub(c: Club) { this.club = c; }
  clubPrev() { const i = CLUB_ORDER.indexOf(this.club); this.club = CLUB_ORDER[(i + CLUB_ORDER.length - 1) % CLUB_ORDER.length]; }
  clubNext() { const i = CLUB_ORDER.indexOf(this.club); this.club = CLUB_ORDER[(i + 1) % CLUB_ORDER.length]; }
  aimBy(deg: number) { this.aimDeg = Math.max(-180, Math.min(180, this.aimDeg + deg)); }
  /** a putt wants a finer hand than a drive: half a degree per press, not three */
  aimStep(): number { return this.club === 'putter' ? 0.5 : 3; }

  meterRampS(): number { return this.club === 'putter' ? 1.6 : 1.0; }
  puttScale(): number {
    const d = this.distToCup();
    return d == null ? 1 : Math.max(0.08, puttPowerFor(d * 1.8));
  }
  /** the physics power behind the meter fraction */
  shotPower(): number { return this.club === 'putter' ? Math.max(0.03, this.power * this.puttScale()) : this.power; }

  swingStart() {
    if (!this.canSwing() || this.charging) return;
    this.charging = true;
    this.chargeStart = performance.now();
  }
  swingEnd() {
    if (!this.charging) return;
    this.charging = false;
    const meter = this.power;
    const p = this.shotPower();
    this.power = 0;
    if (meter > 0.02) this.onSwing(this.club, this.aimDeg, p);
  }
  /** Dragging off the button is a change of mind, not a swing (QC round 2). */
  swingCancel() {
    if (!this.charging) return;
    this.charging = false;
    this.power = 0;
  }

  /** Call every frame: held keys steer the aim; a charging swing runs the meter. */
  update(dt: number) {
    const rate = this.club === 'putter' ? 8 : 30; // degrees per second
    if (this.held.has('KeyA') || this.held.has('ArrowLeft')) this.aimBy(-rate * dt);
    if (this.held.has('KeyD') || this.held.has('ArrowRight')) this.aimBy(rate * dt);
    if (this.charging) {
      const t = (performance.now() - this.chargeStart) / 1000;
      const period = this.meterRampS() * 2;
      const phase = (t % period) / period; // 0..1
      this.power = phase < 0.5 ? phase * 2 : 2 - phase * 2;
    }
  }
}
